import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const RuleImportExport = ({ 
  rules = [], 
  onImport, 
  onExport,
  className = '' 
}) => {
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleExport = () => {
    const exportData = {
      version: "1.0",
      exportDate: new Date()?.toISOString(),
      rules: rules?.map(rule => ({
        name: rule?.name,
        patterns: rule?.patterns,
        patternType: rule?.patternType,
        vendorName: rule?.vendorName,
        confidenceThreshold: rule?.confidenceThreshold,
        priority: rule?.priority,
        isActive: rule?.isActive,
        caseSensitive: rule?.caseSensitive,
        exactMatch: rule?.exactMatch
      }))
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json'
    });
    
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `matching-rules-export-${new Date()?.toISOString()?.split('T')?.[0]}.json`;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
    URL.revokeObjectURL(url);

    onExport?.(exportData);
  };

  const handleFileSelect = (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    if (file?.type !== 'application/json') {
      alert('Please select a JSON file');
      return;
    }

    setImportFile(file);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e?.target?.result);
        validateAndPreviewImport(data);
      } catch (error) {
        alert('Invalid JSON file format');
        setImportFile(null);
      }
    };
    reader?.readAsText(file);
  };

  const validateAndPreviewImport = (data) => {
    const errors = [];
    const warnings = [];

    // Validate structure
    if (!data?.rules || !Array.isArray(data?.rules)) {
      errors?.push('Invalid file structure: missing rules array');
      return;
    }

    // Validate each rule
    const validRules = [];
    data?.rules?.forEach((rule, index) => {
      const ruleErrors = [];
      
      if (!rule?.name || typeof rule?.name !== 'string') {
        ruleErrors?.push(`Rule ${index + 1}: Missing or invalid name`);
      }
      
      if (!rule?.patterns || !Array.isArray(rule?.patterns) || rule?.patterns?.length === 0) {
        ruleErrors?.push(`Rule ${index + 1}: Missing or invalid patterns`);
      }
      
      if (!rule?.vendorName || typeof rule?.vendorName !== 'string') {
        ruleErrors?.push(`Rule ${index + 1}: Missing vendor name`);
      }

      if (ruleErrors?.length === 0) {
        validRules?.push({
          ...rule,
          patternType: rule?.patternType || 'fuzzy',
          confidenceThreshold: rule?.confidenceThreshold || 0.8,
          priority: rule?.priority || 1,
          isActive: rule?.isActive !== undefined ? rule?.isActive : true,
          caseSensitive: rule?.caseSensitive || false,
          exactMatch: rule?.exactMatch || false
        });
      } else {
        errors?.push(...ruleErrors);
      }
    });

    // Check for duplicates
    const existingNames = rules?.map(r => r?.name?.toLowerCase());
    const duplicates = validRules?.filter(rule => 
      existingNames?.includes(rule?.name?.toLowerCase())
    );
    
    if (duplicates?.length > 0) {
      warnings?.push(`${duplicates?.length} rule(s) will overwrite existing rules with same names`);
    }

    setImportPreview({
      totalRules: data?.rules?.length,
      validRules: validRules?.length,
      errors,
      warnings,
      rules: validRules,
      exportDate: data?.exportDate,
      version: data?.version
    });
  };

  const executeImport = async () => {
    if (!importPreview || importPreview?.validRules === 0) return;

    setIsProcessing(true);
    
    try {
      // Simulate processing delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      onImport?.(importPreview?.rules);
      setIsImportModalOpen(false);
      setImportFile(null);
      setImportPreview(null);
    } catch (error) {
      alert('Import failed: ' + error?.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const closeImportModal = () => {
    setIsImportModalOpen(false);
    setImportFile(null);
    setImportPreview(null);
  };

  return (
    <>
      <div className={`flex items-center space-x-3 ${className}`}>
        <Button
          variant="outline"
          onClick={() => setIsImportModalOpen(true)}
          iconName="Upload"
          iconPosition="left"
        >
          Import Rules
        </Button>
        
        <Button
          variant="outline"
          onClick={handleExport}
          disabled={rules?.length === 0}
          iconName="Download"
          iconPosition="left"
        >
          Export Rules ({rules?.length})
        </Button>
      </div>
      {/* Import Modal */}
      {isImportModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-background/80 backdrop-blur-sm"
            onClick={closeImportModal}
          />
          
          <div className="relative bg-card border border-border rounded-lg shadow-moderate max-w-2xl w-full mx-4">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-foreground">Import Matching Rules</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={closeImportModal}
                  iconName="X"
                />
              </div>

              {/* File Selection */}
              {!importFile && (
                <div className="space-y-4">
                  <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                    <Icon name="Upload" size={48} className="mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">
                      Select Rules File
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Choose a JSON file exported from Invoice Parser Pro
                    </p>
                    
                    <Input
                      type="file"
                      accept=".json"
                      onChange={handleFileSelect}
                      className="max-w-xs mx-auto"
                    />
                  </div>

                  <div className="text-xs text-muted-foreground space-y-1">
                    <p>• Supported format: JSON files from Invoice Parser Pro exports</p>
                    <p>• Existing rules with matching names will be overwritten</p>
                    <p>• Invalid rules will be skipped during import</p>
                  </div>
                </div>
              )}

              {/* Import Preview */}
              {importPreview && (
                <div className="space-y-4">
                  <div className="flex items-center space-x-2 mb-4">
                    <Icon name="FileText" size={20} className="text-primary" />
                    <span className="font-medium text-foreground">{importFile?.name}</span>
                    <span className="text-sm text-muted-foreground">
                      ({(importFile?.size / 1024)?.toFixed(1)} KB)
                    </span>
                  </div>

                  {/* Summary */}
                  <div className="grid grid-cols-3 gap-4 p-4 bg-muted/30 rounded-lg">
                    <div className="text-center">
                      <div className="text-2xl font-semibold text-foreground">
                        {importPreview?.totalRules}
                      </div>
                      <div className="text-xs text-muted-foreground">Total Rules</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-semibold text-success">
                        {importPreview?.validRules}
                      </div>
                      <div className="text-xs text-muted-foreground">Valid Rules</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-semibold text-error">
                        {importPreview?.errors?.length}
                      </div>
                      <div className="text-xs text-muted-foreground">Errors</div>
                    </div>
                  </div>

                  {/* Export Info */}
                  {importPreview?.exportDate && (
                    <div className="text-sm text-muted-foreground">
                      <p>Export Date: {new Date(importPreview.exportDate)?.toLocaleDateString()}</p>
                      {importPreview?.version && <p>Version: {importPreview?.version}</p>}
                    </div>
                  )}

                  {/* Errors */}
                  {importPreview?.errors?.length > 0 && (
                    <div className="p-3 bg-error/10 border border-error/20 rounded-md">
                      <h4 className="text-sm font-medium text-error mb-2">
                        <Icon name="AlertTriangle" size={16} className="inline mr-1" />
                        Import Errors
                      </h4>
                      <ul className="text-xs text-error space-y-1">
                        {importPreview?.errors?.map((error, index) => (
                          <li key={index}>• {error}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Warnings */}
                  {importPreview?.warnings?.length > 0 && (
                    <div className="p-3 bg-warning/10 border border-warning/20 rounded-md">
                      <h4 className="text-sm font-medium text-warning mb-2">
                        <Icon name="AlertCircle" size={16} className="inline mr-1" />
                        Warnings
                      </h4>
                      <ul className="text-xs text-warning space-y-1">
                        {importPreview?.warnings?.map((warning, index) => (
                          <li key={index}>• {warning}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Valid Rules Preview */}
                  {importPreview?.validRules > 0 && (
                    <div className="max-h-48 overflow-y-auto border border-border rounded-md">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50 sticky top-0">
                          <tr>
                            <th className="text-left p-2 font-medium">Rule Name</th>
                            <th className="text-left p-2 font-medium">Vendor</th>
                            <th className="text-left p-2 font-medium">Patterns</th>
                          </tr>
                        </thead>
                        <tbody>
                          {importPreview?.rules?.slice(0, 10)?.map((rule, index) => (
                            <tr key={index} className="border-t border-border">
                              <td className="p-2 font-medium">{rule?.name}</td>
                              <td className="p-2 text-muted-foreground">{rule?.vendorName}</td>
                              <td className="p-2 text-muted-foreground">
                                {rule?.patterns?.length} pattern{rule?.patterns?.length !== 1 ? 's' : ''}
                              </td>
                            </tr>
                          ))}
                          {importPreview?.rules?.length > 10 && (
                            <tr>
                              <td colSpan={3} className="p-2 text-center text-muted-foreground text-xs">
                                ... and {importPreview?.rules?.length - 10} more rules
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-end space-x-3 mt-6 pt-4 border-t border-border">
                <Button
                  variant="outline"
                  onClick={closeImportModal}
                  disabled={isProcessing}
                >
                  Cancel
                </Button>
                
                {importFile && !importPreview && (
                  <Button
                    variant="ghost"
                    onClick={() => setImportFile(null)}
                  >
                    Choose Different File
                  </Button>
                )}
                
                {importPreview && importPreview?.validRules > 0 && (
                  <Button
                    variant="default"
                    onClick={executeImport}
                    loading={isProcessing}
                    iconName="Upload"
                    iconPosition="left"
                  >
                    Import {importPreview?.validRules} Rules
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RuleImportExport;
